import React, { useState, useEffect, useRef, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { useI18n } from "./i18n";
import NavIcon from "./NavIcons";
import { fetchNotifications, fetchUnreadCount, markNotificationRead, markAllNotificationsRead } from "./client";
import { useNotificationSocket } from "./useNotificationSocket";

const fadeDown = keyframes`from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}`;
const pop = keyframes`0%{transform:scale(1)}50%{transform:scale(1.3)}100%{transform:scale(1)}`;

const Wrap = styled.div`
  position: relative;
`;

const BellBtn = styled.button`
  position: relative;
  display: flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; border-radius: 10px; border: none;
  background: transparent; color: ${({theme}) => theme.text};
  cursor: pointer;
  &:hover { background: ${({theme}) => theme.hoverBg || "rgba(128,128,128,.12)"}; }
`;

const Badge = styled.span`
  position: absolute; top: 2px; right: 2px;
  min-width: 16px; height: 16px; padding: 0 3px;
  border-radius: 999px; background: ${({theme}) => theme.red || "#e5484d"};
  color: #fff; font-size: .62rem; font-weight: 700; line-height: 16px; text-align: center;
  animation: ${pop} .25s ease;
`;

const Dropdown = styled.div`
  position: absolute; top: calc(100% + 10px); right: 0;
  width: 340px; max-width: 90vw; max-height: 420px; overflow-y: auto;
  background: ${({theme}) => theme.fonNav}; border: 1px solid ${({theme}) => theme.border};
  border-radius: 14px; box-shadow: 0 12px 32px rgba(0,0,0,.28);
  animation: ${fadeDown} .15s ease; z-index: 300;

  @media (max-width: 480px) {
    position: fixed; top: 56px; right: 8px; left: 8px; width: auto;
  }
`;

const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: .75rem 1rem; border-bottom: 1px solid ${({theme}) => theme.border};
  font-weight: 600; font-size: .85rem;
`;

const MarkAllBtn = styled.button`
  background: none; border: none; cursor: pointer;
  color: ${({theme}) => theme.green}; font-size: .75rem; font-weight: 600;
  &:disabled { opacity: .4; cursor: default; }
`;

const Item = styled.div`
  display: flex; gap: .6rem; padding: .7rem 1rem; cursor: pointer;
  border-bottom: 1px solid ${({theme}) => theme.border};
  background: ${({theme, $unread}) => $unread ? (theme.hoverBg || "rgba(46,204,113,.06)") : "transparent"};
  &:hover { background: ${({theme}) => theme.hoverBg || "rgba(128,128,128,.08)"}; }
  &:last-child { border-bottom: none; }
`;

const Dot = styled.div`
  width: 8px; height: 8px; border-radius: 999px; margin-top: 6px; flex-shrink: 0;
  background: ${({theme, $unread}) => $unread ? theme.green : "transparent"};
`;

const ItemBody = styled.div`
  flex: 1; min-width: 0;
`;

const ItemTitle = styled.div`
  font-size: .82rem; font-weight: 600; color: ${({theme}) => theme.text};
`;

const ItemText = styled.div`
  font-size: .78rem; color: ${({theme}) => theme.textSecondary || "#8a97a3"};
  margin-top: 2px; line-height: 1.35;
`;

const ItemTime = styled.div`
  font-size: .68rem; color: ${({theme}) => theme.textSecondary || "#8a97a3"};
  margin-top: 4px;
`;

const Empty = styled.div`
  padding: 2.5rem 1rem; text-align: center; font-size: .82rem;
  color: ${({theme}) => theme.textSecondary || "#8a97a3"};
`;

function timeAgo(iso, lang) {
  const diffSec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(lang || "fr", { numeric: "auto" });
  if (diffSec < 60)   return rtf.format(-Math.floor(diffSec), "second");
  if (diffSec < 3600)  return rtf.format(-Math.floor(diffSec / 60), "minute");
  if (diffSec < 86400) return rtf.format(-Math.floor(diffSec / 3600), "hour");
  return rtf.format(-Math.floor(diffSec / 86400), "day");
}

export default function NotificationBell({ user, navigate }) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const wrapRef = useRef(null);

  const loadInbox = useCallback(async () => {
    try {
      const res = await fetchNotifications({ limit: 20 });
      setItems(res.data?.results || res.data || []);
      setLoaded(true);
    } catch {
      // silencieux — la cloche reste utilisable, juste vide
    }
  }, []);

  const loadUnreadCount = useCallback(async () => {
    try {
      const res = await fetchUnreadCount();
      setUnreadCount(res.data?.count || 0);
    } catch {}
  }, []);

  // Chargement initial + rafraîchissement du compteur au login
  useEffect(() => {
    if (!user) { setItems([]); setUnreadCount(0); return; }
    loadUnreadCount();
  }, [user, loadUnreadCount]);

  // Temps réel : toute nouvelle notification arrive ici sans recharger la page
  useNotificationSocket(user, (notification) => {
    setItems((prev) => [notification, ...prev]);
    setUnreadCount((c) => c + 1);
  });

  // Charge l'historique complet seulement à l'ouverture (pas de requête inutile en arrière-plan)
  useEffect(() => {
    if (open && !loaded) loadInbox();
  }, [open, loaded, loadInbox]);

  // Fermer au clic en dehors
  useEffect(() => {
    function onClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const handleItemClick = async (notif) => {
    if (!notif.is_read) {
      setItems((prev) => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
      markNotificationRead(notif.id).catch(() => {});
    }
    if (!navigate) return;

    // Marché résolu / trade exécuté / alerte de prix / réponse à un commentaire
    // → toutes rattachées à un marché précis
    const marketTypes = ["market_resolved", "trade_executed", "price_alert", "comment_reply"];
    if (marketTypes.includes(notif.notif_type) && notif.data?.market_id) {
      setOpen(false);
      navigate("markets", { selectedId: parseInt(notif.data.market_id, 10) });
      return;
    }

    // Dépôt crédité / statut de retrait → portefeuille
    if (["deposit_credited", "withdrawal_update"].includes(notif.notif_type)) {
      setOpen(false);
      navigate("wallet");
      return;
    }

    // Tickets : accusé de réception / réponse du support → page Support (mes tickets)
    const type = notif.notif_type || notif.category;
    if (["support_reply", "ticket_created", "ticket_reply"].includes(type)) {
      setOpen(false);
      navigate("support");
      return;
    }

    // Alerte côté équipe (nouveau ticket / réponse utilisateur / message de contact)
    // → espace employé pour le personnel, sinon page Support
    if (["ticket_support_alert", "contact_support_alert"].includes(type)) {
      setOpen(false);
      let staff = false;
      try { staff = localStorage.getItem("is_staff") === "true" || localStorage.getItem("is_admin") === "true"; } catch {}
      navigate(staff ? "employee" : "support");
      return;
    }

    // new_market / newsletter / security_alert : pas de destination évidente,
    // on marque juste comme lu sans naviguer.
  };

  const handleMarkAll = async () => {
    setItems((prev) => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
    markAllNotificationsRead().catch(() => {});
  };

  if (!user) return null;

  return (
    <Wrap ref={wrapRef}>
      <BellBtn onClick={() => setOpen(o => !o)} title={t("notif.title")}>
        <NavIcon name="bell" size={18} />
        {unreadCount > 0 && <Badge key={unreadCount}>{unreadCount > 99 ? "99+" : unreadCount}</Badge>}
      </BellBtn>

      {open && (
        <Dropdown>
          <Header>
            <span>{t("notif.title")}</span>
            <MarkAllBtn onClick={handleMarkAll} disabled={unreadCount === 0}>
              {t("notif.markAllRead")}
            </MarkAllBtn>
          </Header>

          {items.length === 0 ? (
            <Empty>{t("notif.empty")}</Empty>
          ) : (
            items.map((notif) => (
              <Item key={notif.id} $unread={!notif.is_read} onClick={() => handleItemClick(notif)}>
                <Dot $unread={!notif.is_read} />
                <ItemBody>
                  <ItemTitle>{notif.title}</ItemTitle>
                  <ItemText>{notif.body}</ItemText>
                  <ItemTime>{timeAgo(notif.created_at, lang)}</ItemTime>
                </ItemBody>
              </Item>
            ))
          )}
        </Dropdown>
      )}
    </Wrap>
  );
}
